// KidRequestableSection — inline kid-axis editor for an MCMItemCard.
// Mirrors the per-row controls in KidPantryManager so a parent can
// flag THIS specific pantry item as kid-requestable without leaving
// the item sheet (the manager was buried under Settings → Kids Mode
// → KID ITEMS). Writes go through the parent's onChange as a single
// patch payload; persistence is the parent's job.
//
// Three controls:
//   1. Toggle — flips kidVisible. When off, the label + kind row
//      stays collapsed.
//   2. Label  — kid-facing nickname ("Juicy Pouch" for Capri Sun).
//      Debounced 380ms so typing doesn't thrash writes. Echo-guarded
//      against round-trip stomps via lastWroteRef (see CLAUDE.md
//      "controlled inputs with debounced writes need an echo guard").
//   3. Kind   — Meal / Snack / Treat as a three-up chip rail.
//
// Visual tokens follow MCMItemCard (serif italic chips, ≥12px text,
// no emoji). The KidPantryManager renders kind emojis because it
// lives inside the kids-mode emoji exception; this sheet lives in
// the main app where emojis are banned.

import { useEffect, useMemo, useRef, useState } from "react";
import { font, space, axis, inline, field } from "./tokens";
import { withAlpha } from "./primitives";
import { supabase } from "../../lib/supabase";
import { findIngredient } from "../../data/ingredients";
import { cardForKitchenItem } from "../../lib/cardsForRecipe";
import CardPreviewChips from "../../components/CardPreviewChips";

const KIND_OPTIONS = [
  { key: "meal",    label: "Meal" },
  { key: "snack",   label: "Snack" },
  { key: "dessert", label: "Treat" },
];

export function KidRequestableSection({
  theme,
  displayName,
  canonicalId,
  kidVisible,
  kidLabel,
  kidKind,
  onChange,
}) {
  const enabled = !!kidVisible;
  const kind = kidKind || "snack";

  // Predict what card type eating this single item will award. One
  // item = one card. Memoized so re-renders don't re-resolve the
  // canonical row needlessly.
  const prediction = useMemo(() => {
    if (!enabled || !canonicalId) return null;
    const { kind: cardKind, count } = cardForKitchenItem(
      { canonical_id: canonicalId, name: displayName },
      { getIngredient: findIngredient },
    );
    if (!cardKind) return null;
    const base = { build: 0, super: 0, boost: 0, spark: 0, dairy: 0, water: 0 };
    return { ...base, [cardKind]: count };
  }, [enabled, canonicalId, displayName]);

  const [localLabel, setLocalLabel] = useState(kidLabel || "");
  const lastWroteRef = useRef(kidLabel || "");

  useEffect(() => {
    const v = kidLabel || "";
    if (v === lastWroteRef.current) return;
    setLocalLabel(v);
  }, [kidLabel]);

  useEffect(() => {
    if (!enabled) return undefined;
    const v = kidLabel || "";
    if (localLabel === v) return undefined;
    const id = setTimeout(() => {
      const next = localLabel.trim() || null;
      lastWroteRef.current = next || "";
      onChange?.({ kidLabel: next });
    }, 380);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLabel, enabled]);

  const toggle = () => {
    if (enabled) {
      onChange?.({ kidVisible: false });
    } else {
      onChange?.({ kidVisible: true, kidKind: kidKind || "snack" });
      // Fire-and-forget generation: voiceline + 3D-emoji icon.
      //
      // Voiceline path: for every voice_id in pet_catalog, ElevenLabs
      // renders "<kid_label or canonical name>" and caches in
      // canonical_voicelines (cat species today, more once additional
      // voice IDs are uploaded).
      //
      // Emoji path: gpt-image-1 renders a 3D-cartoon emoji-style
      // icon for the canonical and writes the URL to
      // ingredient_info.info.imageUrl. The canonicalImageUrlFor
      // resolver picks it up automatically — kid pantry tile,
      // MCMItemCard header, anywhere icons render.
      //
      // Both edge fns are idempotent: if the audio row OR the
      // imageUrl already exists, the call no-ops. So re-flipping
      // kid_visible doesn't burn money.
      if (canonicalId) {
        supabase.functions.invoke("generate-canonical-voicelines", {
          body: {
            canonicalId,
            kidLabel:     kidLabel || null,
            fallbackText: displayName || null,
          },
        }).catch((err) => {
          console.warn("[kid-voicelines] invoke failed:", err?.message || err);
        });
        supabase.functions.invoke("generate-canonical-emoji", {
          body: {
            canonicalId,
            canonicalName: displayName || canonicalId.replace(/_/g, " "),
            kidLabel:      kidLabel || null,
          },
        }).catch((err) => {
          console.warn("[kid-emoji] invoke failed:", err?.message || err);
        });
      }
    }
  };

  const tone = axis.canonical;

  return (
    <section style={{ marginTop: space.gap }}>
      <button
        type="button"
        onClick={toggle}
        className="mcm-focusable"
        style={{
          ...inline(space.flow),
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `${space.flow}px ${space.gap}px`,
          background: enabled ? withAlpha(tone, 0.14) : "transparent",
          border: `1px ${enabled ? "solid" : "dashed"} ${withAlpha(tone, 0.55)}`,
          borderRadius: 12,
          cursor: "pointer",
          touchAction: "manipulation",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left", minWidth: 0 }}>
          <div style={{
            fontFamily: font.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.color.inkFaint,
          }}>
            {enabled ? "On kids menu" : "Add to kids menu"}
          </div>
          <div style={{
            fontFamily: font.serif,
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: 15,
            color: theme.color.ink,
          }}>
            {enabled ? "Kids can request this" : "Let kids request this"}
          </div>
        </div>
        <div
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: 8,
            background: enabled ? tone : "transparent",
            border: `2px solid ${enabled ? tone : withAlpha(tone, 0.55)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontFamily: font.sans,
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {enabled ? "✓" : ""}
        </div>
      </button>

      {enabled && (
        <div style={{ ...inline(space.tight), flexDirection: "column", marginTop: space.flow }}>
          <input
            type="text"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            placeholder={`What kids call it (default: ${displayName || "this item"})`}
            maxLength={48}
            style={{
              ...field,
              fontFamily: font.serif,
              fontStyle: "italic",
              fontSize: 15,
              boxSizing: "border-box",
            }}
          />
          <div style={{ ...inline(space.tight), marginTop: space.tight }}>
            {KIND_OPTIONS.map((k) => {
              const active = kind === k.key;
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => onChange?.({ kidKind: k.key })}
                  className="mcm-focusable"
                  style={{
                    flex: 1,
                    padding: `${space.flow}px ${space.tight}px`,
                    fontFamily: font.mono,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    color: active ? "#fff" : theme.color.ink,
                    background: active ? tone : "transparent",
                    border: `1.5px solid ${withAlpha(tone, 0.55)}`,
                    borderRadius: 10,
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  {k.label}
                </button>
              );
            })}
          </div>
          <CardPreviewChips prediction={prediction} compact />
        </div>
      )}
    </section>
  );
}

export default KidRequestableSection;
